import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Heart, User, ExternalLink } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import ProductCard from "../components/ProductCard";
import { useCart } from "../contexts/CartContext";

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://lirkprint-backend.onrender.com").replace(/\/$/, "");
const API = `${BASE_URL}/api`;

const STATUS_COLORS = {
  pending: "text-yellow-400",
  processing: "text-blue-400",
  shipped: "text-purple-400",
  delivered: "text-green-400",
  cancelled: "text-red-400",
};

export default function Profile() {
  const { user } = useAuth();
  const { lang, t } = useLanguage();
  const { addItem } = useCart();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, wishlistRes] = await Promise.all([
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/wishlist`, { withCredentials: true })
      ]);
      setOrders(ordersRes.data.orders || []);
      setWishlist(wishlistRes.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
    setWishlist(prev => prev.filter(p => p.id !== productId));
  };

  const tabs = [
    { key: "orders", label: t("profile.orders"), icon: Package },
    { key: "wishlist", label: t("profile.wishlist"), icon: Heart },
    { key: "account", label: t("profile.settings"), icon: User },
  ];

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <span className="text-[11px] font-mono tracking-[0.3em] uppercase text-foreground/30 mb-2 block">
            {user?.email}
          </span>
          <h1 className="text-4xl font-heading font-bold tracking-tighter text-foreground">
            {user?.name}
          </h1>
        </motion.div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-10">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`profile-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-6 py-4 text-xs font-mono tracking-widest uppercase transition-all ${
                tab === key
                  ? "text-foreground border-b-2 border-foreground -mb-px"
                  : "text-foreground/40 hover:text-foreground"
              }`}
            >
              <Icon size={14} strokeWidth={1.5} /> {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Orders */}
            {tab === "orders" && (
              <div data-testid="orders-list">
                {orders.length === 0 ? (
                  <div className="text-center py-24">
                    <Package size={48} strokeWidth={1} className="text-foreground/20 mx-auto mb-4" />
                    <p className="text-foreground/40 font-mono">{t("profile.no_orders")}</p>
                    <Link to="/shop" className="inline-block mt-6 px-6 py-3 bg-foreground text-background text-xs font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all">
                      {lang === "de" ? "Zum Shop" : "Go to Shop"}
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map(order => (
                      <motion.div
                        key={order.id}
                        data-testid={`order-${order.id}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-6 bg-surface border border-border rounded-sm"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="text-xs font-mono text-foreground/30">#{order.id?.slice(-8).toUpperCase()}</p>
                            <p className="text-xs text-foreground/40 mt-1">{new Date(order.created_at).toLocaleDateString(lang)}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs font-mono uppercase ${STATUS_COLORS[order.status] || "text-foreground/40"}`}>
                              {t(`profile.status.${order.status}`) || order.status}
                            </span>
                            <p className="text-base font-mono text-foreground mt-1">€{order.total?.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(order.items || []).map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-foreground/50">
                              {item.image && <img src={item.image} alt="" className="w-8 h-8 object-cover rounded-sm" />}
                              <span>{lang === "de" ? item.name_de : item.name_en} ×{item.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Wishlist */}
            {tab === "wishlist" && (
              <div data-testid="wishlist-grid">
                {wishlist.length === 0 ? (
                  <div className="text-center py-24">
                    <Heart size={48} strokeWidth={1} className="text-foreground/20 mx-auto mb-4" />
                    <p className="text-foreground/40 font-mono">{lang === "de" ? "Deine Wunschliste ist leer" : "Your wishlist is empty"}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {wishlist.map((product, i) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        index={i}
                        onAddToCart={addItem}
                        onToggleWishlist={() => removeFromWishlist(product.id)}
                        isInWishlist={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Account */}
            {tab === "account" && (
              <div data-testid="account-info" className="max-w-md space-y-6">
                <div className="p-6 bg-surface border border-border rounded-sm space-y-4">
                  <div>
                    <p className="text-xs font-mono tracking-wider text-foreground/30 uppercase mb-1">{lang === "de" ? "Name" : "Name"}</p>
                    <p className="text-foreground">{user?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono tracking-wider text-foreground/30 uppercase mb-1">Email</p>
                    <p className="text-foreground">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono tracking-wider text-foreground/30 uppercase mb-1">Role</p>
                    <p className="text-foreground capitalize">{user?.role}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div className="pb-24" />
      </div>
    </div>
  );
}
